import utc from 'dayjs/plugin/utc';
import dayjs, { Dayjs } from 'dayjs';
import { format, getTime } from 'date-fns';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { CalendarConfig } from 'src/services/config';

import { PickerType } from 'src/components/SwitchableDatePicker';

import mockCalendar from './mock-calendar.json';
import { formatDateByFrequency as formatDateByFreq } from './frequency';
import {
  DATE_FORMAT,
  DATE_END_FORMAT,
  DATE_START_FORMAT,
  DATE_TIME_MINUTE_END_FORMAT,
  DATE_TIME_MINUTE_START_FORMAT,
} from './dateFormats';

dayjs.extend(relativeTime);
dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

type InputValue = Date | string | number | null | undefined;

interface WeekData {
  sequence: number;
  weeklySequence: number;
  startDate: string;
  endDate: string;
}

interface CalendarData {
  [year: string]: WeekData[];
}

/**
 * 格式化日期
 */
export function fDate(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'yyyy-MM-dd';
  return date ? format(new Date(date), fm) : '';
}

/**
 * 格式化时间
 */
export function fTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'p';
  const time = date ? format(new Date(date), fm) : '';
  return time.replace('PM', '下午').replace('AM', '上午');
}

/**
 * 格式化日期时间
 */
export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy p';
  return date ? format(new Date(date), fm) : '';
}

/**
 * 获取时间戳
 */
export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : '';
}

/**
 * 获取相对时间
 */
export function fToNow(date: InputValue) {
  if (date) {
    const dateStr = dayjs().from(date);
    return dateStr ? dateStr.replace('内', '之前') : dateStr;
  }
  return '';
}

/**
 * 检查日期是否在两个日期之间
 */
export function isBetween(inputDate: Date | string | number, startDate: Date, endDate: Date) {
  const date = new Date(inputDate);
  const results =
    new Date(date.toDateString()) >= new Date(startDate.toDateString()) &&
    new Date(date.toDateString()) <= new Date(endDate.toDateString());
  return results;
}

/**
 * 检查日期是否在另一个日期之后
 */
export function isAfter(startDate: Date | null, endDate: Date | null) {
  const results =
    startDate && endDate ? new Date(startDate).getTime() > new Date(endDate).getTime() : false;
  return results;
}

type dateInputValue = dayjs.Dayjs | Date | string | number | null | undefined;

/**
 * 根据频率类型格式化日期
 */
export function formatDateByFrequency(date: dateInputValue, frequencyType?: string): string {
  if (!date) return '';
  return formatDateByFreq(date, frequencyType || '');
}

/**
 * 根据频率类型格式化日期范围
 */
export function formatDateRangeByFrequency(
  startDate: InputValue,
  endDate: InputValue,
  year: number,
  sequence: number,
  frequencyType?: string,
  calendarConfig?: CalendarConfig | null
): string {
  // 如果没有年份和序列信息，则使用原有的startDate逻辑
  if (!year || !sequence) {
    if (!startDate || !endDate) return '';

    const startLocalStr =
      typeof startDate === 'string' ? convertToLocalTimezone(startDate) : startDate;
    const startDayjs = dayjs(startLocalStr);

    if (frequencyType?.toLowerCase() === 'weekly') {
      const month = startDayjs.format('MM');
      return `${year}年${month}月第${sequence}周`;
    }

    return formatDateByFreq(startDayjs, frequencyType || '');
  }

  // 优先使用 startYear 和 sequence 来计算正确的日期
  let calculatedDate: dayjs.Dayjs;

  switch (frequencyType?.toLowerCase()) {
    case 'yearly':
      calculatedDate = dayjs(`${year}-01-01`);
      break;
    case 'quarterly': {
      const quarterStartMonth = (sequence - 1) * 3 + 1;
      calculatedDate = dayjs(`${year}-${quarterStartMonth.toString().padStart(2, '0')}-01`);
      break;
    }
    case 'monthly':
      calculatedDate = dayjs(`${year}-${sequence.toString().padStart(2, '0')}-01`);
      break;
    case 'weekly': {
      // 使用 mock-calendar.json 数据来获取正确的月份和周序号
      const calendar = calendarConfig || (mockCalendar as CalendarData);
      const weekDataList = calendar[year.toString()] as WeekData[];

      if (weekDataList) {
        const weekData = weekDataList.find((w) => w.sequence === sequence);
        if (weekData) {
          const month = Number(weekData.startDate.split('-')[1]);
          return `${year}年${month}月第${weekData.weeklySequence}周`;
        }
      }

      // 如果在 calendar 数据中找不到对应的周数据，使用原有逻辑作为后备
      calculatedDate = dayjs(`${year}-01-01`).isoWeek(sequence);
      const month = calculatedDate.format('MM');

      // 计算该周在所属月份中的周数
      const monthStart = calculatedDate.startOf('month');
      const monthFirstWeek = monthStart.isoWeek();
      const currentWeek = calculatedDate.isoWeek();

      // 处理跨年的情况
      let weekInMonth;
      if (currentWeek >= monthFirstWeek) {
        weekInMonth = currentWeek - monthFirstWeek + 1;
      } else {
        // 跨年情况：当前周数小于月初周数（例如1月包含去年最后一周）
        const weeksInPrevYear = dayjs(`${year - 1}-12-31`).isoWeek();
        weekInMonth = currentWeek + weeksInPrevYear - monthFirstWeek + 1;
      }

      return `${year}年${month}月第${weekInMonth}周`;
    }
    default:
      // 如果无法识别频率类型，使用年份和序列的默认逻辑
      calculatedDate = dayjs(`${year}-01-01`);
  }

  return formatDateByFreq(calculatedDate, frequencyType || '');
}

/**
 * 将日期转换为范围
 */
export function convertDateToRange(
  statementDate: Dayjs,
  calendarType: PickerType
): { startDate: Dayjs; endDate: Dayjs } {
  let startDate: Dayjs;
  let endDate: Dayjs;

  switch (calendarType) {
    case 'year':
      startDate = statementDate.startOf('year');
      endDate = statementDate.endOf('year');
      break;
    case 'quarter':
      startDate = statementDate.startOf('quarter');
      endDate = statementDate.endOf('quarter');
      break;
    case 'month':
      startDate = statementDate.startOf('month');
      endDate = statementDate.endOf('month');
      break;
    case 'week':
      startDate = statementDate.startOf('week');
      endDate = statementDate.endOf('week');
      break;
    default:
      throw new Error(`Unsupported calendar type: ${calendarType}`);
  }

  return { startDate, endDate };
}

/**
 * 计算序列号
 */
export function calculateSequence(
  type: PickerType,
  statementDate: Dayjs | null,
  calendarConfig?: CalendarConfig | null
): number {
  if (!statementDate || !type) return 0;
  switch (type) {
    case 'year':
      return 1;
    case 'quarter':
      return Math.floor(statementDate.month() / 3) + 1;
    case 'month':
      return statementDate.month() + 1;
    case 'week':
      // 如果有calendarConfig，使用它来获取正确的sequence
      if (calendarConfig && calendarConfig[statementDate.year()]) {
        const weekData = calendarConfig[statementDate.year()].find(
          (w: WeekData) =>
            statementDate.isSameOrAfter(dayjs(w.startDate)) &&
            statementDate.isSameOrBefore(dayjs(w.endDate))
        );
        if (weekData) {
          return weekData.sequence;
        }
      }
      // 如果没有calendarConfig或找不到对应的周数据，使用原有逻辑作为后备
      return statementDate.week();
    default:
      throw new Error(`Unsupported calendar type: ${type}`);
  }
}

/**
 * 将UTC时间转换为本地时区
 */
function convertToLocalTimezone(utcDateString: string): string {
  return dayjs(utcDateString).local().toISOString();
}

export const dateFormat = (
  value?: string | Date | Dayjs,
  formatStr?: string,
  isUtc: boolean = false
) =>
  // eslint-disable-next-line no-nested-ternary
  value
    ? isUtc
      ? dayjs.utc(value).format(formatStr || DATE_FORMAT)
      : dayjs(value).format(formatStr || DATE_FORMAT)
    : '-';

export const transformRangePickerDate = (value: [string, string], initFormat?: boolean) => {
  if (value?.filter(Boolean)?.length === 0) {
    return undefined;
  }

  const arr = [...value];
  if (initFormat === false) {
    // 转换带时分秒的值
    return {
      min: new Date(dateFormat(arr[0], DATE_TIME_MINUTE_START_FORMAT)),
      max: new Date(dateFormat(arr[1], DATE_TIME_MINUTE_END_FORMAT)),
    };
  } // 用于日期 但是又要精确到最大最小值
  return {
    min: new Date(dateFormat(arr[0], DATE_START_FORMAT)),
    max: new Date(dateFormat(arr[1], DATE_END_FORMAT)),
  };
};
